/**
 * Distinct-signer sets (z8nrz7dt44), client side.
 *
 * Signature fields sharing a `distinctSignerSet` name must each be signed by a
 * different person. The server is the authority (cgt-backend
 * `form-engine/distinct-signers.ts`, same rule); this lets the renderer block
 * Apply Signature BEFORE a password is asked for (AC4), and the builder show
 * which fields a set ties together.
 *
 * Identity is the stamped `signedByUserId` — never the display name, which two
 * people can share. Legacy values without it are never attributed.
 */

interface SignatureLike {
  id: string
  label: string
  fieldType?: string
  type?: string
  distinctSignerSet?: string
}

/** The set a signature field belongs to, or null. Works on saved and builder shapes. */
export function distinctSignerSetOf(field: SignatureLike): string | null {
  const isSignature = (field.fieldType ?? field.type) === 'signature'
  const name = field.distinctSignerSet?.trim()
  return isSignature && name ? name : null
}

/** The other signature fields in `field`'s set, in order. */
export function distinctSetPeers<T extends SignatureLike>(fields: readonly T[], field: SignatureLike): T[] {
  const set = distinctSignerSetOf(field)
  if (!set) return []
  return fields.filter(f => f.id !== field.id && distinctSignerSetOf(f) === set)
}

/**
 * The label of a peer field `userId` already signed, or null. What blocks
 * Apply Signature for the signed-in user.
 */
export function alreadySignedInSet(
  fields: readonly SignatureLike[],
  values: Record<string, unknown>,
  field: SignatureLike,
  userId: string | null,
): string | null {
  if (!userId) return null
  for (const peer of distinctSetPeers(fields, field)) {
    const v = values[peer.id] as { signedByUserId?: unknown } | undefined
    if (v?.signedByUserId === userId) return peer.label
  }
  return null
}

/** Set names already used in a form, for the builder's suggestions. */
export function distinctSetNames(fields: readonly SignatureLike[]): string[] {
  return [...new Set(fields.map(distinctSignerSetOf).filter((n): n is string => !!n))].sort()
}
